import { useState, useEffect } from 'react';
import api from '../../hooks/useApi';
import { formatMoney, formatDate } from '../../utils/format';
import { useToast } from '../ui/Toast';
import Modal from '../shared/Modal';

// ── Helpers ────────────────────────────────────────────────────────────────────

function blankCard()  { return { name: '', client: '', effective_from: '', is_active: false }; }
function blankBand()  { return { band_code: '', band_name: '', day_rate: '' }; }

// ── Main component ─────────────────────────────────────────────────────────────

export default function RateCardPage() {
  const { toast } = useToast();
  const [cards,      setCards]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [expanded,   setExpanded]   = useState(null); // card id

  // Card modal state
  const [cardModal,  setCardModal]  = useState(null); // null | { mode:'create'|'edit', card?:{} }
  const [cardForm,   setCardForm]   = useState(blankCard());
  const [savingCard, setSavingCard] = useState(false);

  // Band modal state
  const [bandModal,  setBandModal]  = useState(null); // null | { mode, cardId, band? }
  const [bandForm,   setBandForm]   = useState(blankBand());
  const [savingBand, setSavingBand] = useState(false);

  const load = () => {
    setLoading(true);
    api.get('rate-cards')
      .then(r => { setCards(r); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(load, []);

  // ── Card actions ──

  const openCreateCard = () => {
    setCardForm(blankCard());
    setCardModal({ mode: 'create' });
  };

  const openEditCard = (card) => {
    setCardForm({ name: card.name, client: card.client, effective_from: card.effective_from, is_active: !!card.is_active });
    setCardModal({ mode: 'edit', card });
  };

  const submitCard = async () => {
    if (!cardForm.name.trim())           { toast.error('Name is required');           return; }
    if (!cardForm.effective_from)        { toast.error('Effective date is required'); return; }
    setSavingCard(true);
    try {
      if (cardModal.mode === 'create') {
        const { id } = await api.post('rate-cards', cardForm);
        setExpanded(id); // auto-open so user can add bands immediately
      } else {
        await api.put(`rate-cards/${cardModal.card.id}`, cardForm);
      }
      load();
      setCardModal(null);
    } catch (e) { toast.error('Save failed: ' + e.message); }
    setSavingCard(false);
  };

  const setActive = async (card) => {
    if (card.is_active) return; // already active
    if (!confirm(`Set "${card.name}" as the active rate card? All other cards will be deactivated.`)) return;
    try {
      await api.put(`rate-cards/${card.id}`, { is_active: true });
      load();
    } catch (e) { toast.error('Failed: ' + e.message); }
  };

  const deleteCard = async (card) => {
    if (!confirm(`Delete "${card.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`rate-cards/${card.id}`);
      load();
    } catch (e) { toast.error('Failed: ' + e.message); }
  };

  // ── Band actions ──

  const openAddBand = (cardId) => {
    setBandForm(blankBand());
    setBandModal({ mode: 'create', cardId });
  };

  const openEditBand = (cardId, band) => {
    setBandForm({ band_code: band.band_code, band_name: band.band_name, day_rate: String(band.day_rate) });
    setBandModal({ mode: 'edit', cardId, band });
  };

  const submitBand = async () => {
    if (!bandForm.band_code.trim())          { toast.error('Band code is required'); return; }
    if (!bandForm.day_rate || isNaN(parseFloat(bandForm.day_rate))) { toast.error('Day rate must be a number'); return; }
    setSavingBand(true);
    try {
      const payload = { band_code: bandForm.band_code.trim().toUpperCase(), band_name: bandForm.band_name.trim() || bandForm.band_code.trim().toUpperCase(), day_rate: parseFloat(bandForm.day_rate) };
      if (bandModal.mode === 'create') {
        await api.post(`rate-cards/${bandModal.cardId}/bands`, payload);
      } else {
        await api.put(`rate-card-bands/${bandModal.band.id}`, payload);
      }
      load();
      setBandModal(null);
    } catch (e) { toast.error('Save failed: ' + e.message); }
    setSavingBand(false);
  };

  const deleteBand = async (band) => {
    if (!confirm(`Delete band "${band.band_code} — ${band.band_name}"?`)) return;
    try {
      await api.delete(`rate-card-bands/${band.id}`);
      load();
    } catch (e) { toast.error('Failed: ' + e.message); }
  };

  // ── Derived ──

  const activeCard = cards.find(c => c.is_active);

  return (
    <div className="fade-in">

      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-extrabold text-navy">Rate Card Management</h2>
          <div className="text-[9px] text-gray-500">Manage rate cards and bands used for resource billing and profitability calculations</div>
        </div>
        <button onClick={openCreateCard} className="bg-navy text-white text-[10px] font-bold px-3 py-1.5 rounded hover:opacity-90 transition-opacity">
          + New Rate Card
        </button>
      </div>

      {/* Active card summary banner */}
      {activeCard && (
        <div className="bg-emerald-50 border border-emerald-200 rounded p-3 mb-4 flex items-center gap-3">
          <div className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
          <div>
            <span className="text-[10px] font-bold text-emerald-800">Active: </span>
            <span className="text-[10px] text-emerald-700">{activeCard.name}</span>
            <span className="text-[9px] text-emerald-500 ml-2">Effective from {formatDate(activeCard.effective_from)}</span>
          </div>
          <div className="ml-auto text-[9px] text-emerald-600 font-semibold">{activeCard.bands?.length || 0} bands</div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-10 text-[10px] text-gray-400">Loading…</div>
      ) : cards.length === 0 ? (
        <div className="text-center py-10 text-[10px] text-gray-400">No rate cards yet. Create one above.</div>
      ) : (
        <div className="space-y-3">
          {cards.map(card => {
            const isActive = !!card.is_active;
            const isOpen   = expanded === card.id;
            return (
              <div key={card.id} className={`bg-white border rounded overflow-hidden transition-shadow hover:shadow-sm ${isActive ? 'border-emerald-300' : 'border-gray-200'}`}>
                {/* Card header row */}
                <div
                  className={`flex items-center px-4 py-3 cursor-pointer ${isActive ? 'bg-emerald-50/60' : 'bg-gray-50/60'}`}
                  onClick={() => setExpanded(isOpen ? null : card.id)}
                >
                  <div className="flex-1 flex items-center gap-3 min-w-0">
                    <span className={`text-[9px] font-bold shrink-0 ${isOpen ? 'rotate-90' : ''} inline-block transition-transform`}>▶</span>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[11px] font-bold text-navy">{card.name}</span>
                        {isActive && (
                          <span className="text-[7px] bg-emerald-500 text-emerald-950 px-1.5 py-0.5 rounded font-bold uppercase tracking-wide">Active</span>
                        )}
                      </div>
                      <div className="text-[9px] text-gray-500 mt-0.5">
                        {card.client && <span className="mr-3">Client: <span className="font-medium text-gray-700">{card.client}</span></span>}
                        <span>Effective: <span className="font-medium text-gray-700">{formatDate(card.effective_from)}</span></span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4 shrink-0" onClick={e => e.stopPropagation()}>
                    <span className="text-[9px] text-gray-400">{card.bands?.length || 0} bands</span>
                    {!isActive && (
                      <button onClick={() => setActive(card)} className="text-[9px] font-semibold text-emerald-600 hover:text-emerald-800 border border-emerald-300 rounded px-2 py-0.5 hover:bg-emerald-50">
                        Set Active
                      </button>
                    )}
                    <button onClick={() => openEditCard(card)} className="text-[9px] font-semibold text-gold hover:underline">Edit</button>
                    {!isActive && (
                      <button onClick={() => deleteCard(card)} className="text-[9px] font-semibold text-red-400 hover:text-red-600 hover:underline">Delete</button>
                    )}
                  </div>
                </div>

                {/* Expanded bands table */}
                {isOpen && (
                  <div className="border-t border-gray-100 px-4 py-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[9px] font-bold text-gray-500 uppercase tracking-wide">Bands</span>
                      <button onClick={() => openAddBand(card.id)} className="text-[9px] font-bold text-gold border border-gold/30 rounded px-2 py-0.5 hover:bg-gold/10 transition-colors">
                        + Add Band
                      </button>
                    </div>
                    {!card.bands || card.bands.length === 0 ? (
                      <p className="text-[9px] text-gray-300 italic py-2">No bands defined. Add one above.</p>
                    ) : (
                      <table className="w-full max-w-2xl">
                        <thead>
                          <tr>
                            <th className="text-left text-[8px] uppercase text-gray-400 font-bold pb-1.5 pr-4">Code</th>
                            <th className="text-left text-[8px] uppercase text-gray-400 font-bold pb-1.5 pr-4">Band Name</th>
                            <th className="text-right text-[8px] uppercase text-gray-400 font-bold pb-1.5 pr-4">Day Rate</th>
                            <th className="text-right text-[8px] uppercase text-gray-400 font-bold pb-1.5">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {card.bands.map(band => (
                            <tr key={band.id} className="border-t border-gray-50 hover:bg-gray-50/50 group">
                              <td className="py-1.5 pr-4">
                                <span className="font-mono text-[9px] font-bold bg-navy/10 text-navy px-1.5 py-0.5 rounded">{band.band_code}</span>
                              </td>
                              <td className="py-1.5 pr-4 text-[10px] text-gray-700">{band.band_name}</td>
                              <td className="py-1.5 pr-4 text-right text-[11px] font-bold text-navy">{formatMoney(band.day_rate, 2)}</td>
                              <td className="py-1.5 text-right">
                                <button onClick={() => openEditBand(card.id, band)} className="text-[9px] text-gold font-semibold hover:underline mr-3 opacity-0 group-hover:opacity-100 transition-opacity">Edit</button>
                                <button onClick={() => deleteBand(band)} className="text-[9px] text-red-400 hover:text-red-600 font-semibold hover:underline opacity-0 group-hover:opacity-100 transition-opacity">Delete</button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="border-t-2 border-gray-200">
                            <td colSpan="2" className="py-1.5 text-[8px] text-gray-400 font-semibold uppercase">Total bands</td>
                            <td className="py-1.5 text-right text-[9px] font-bold text-gray-500">{card.bands.length}</td>
                            <td />
                          </tr>
                        </tfoot>
                      </table>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Rate Card modal ───────────────────────────────────────────────────── */}
      {cardModal && (
        <Modal title={cardModal.mode === 'create' ? 'New Rate Card' : 'Edit Rate Card'} onClose={() => setCardModal(null)}>

            <div className="mb-3">
              <label className="block text-[9px] font-bold text-gray-500 uppercase tracking-wide mb-1">Name *</label>
              <input value={cardForm.name} onChange={e => setCardForm(f => ({ ...f, name: e.target.value }))}
                className="w-full border border-gray-200 rounded px-2 py-1.5 text-[11px] focus:outline-none focus:border-gold"
                placeholder="e.g. RRSL 2027 Rate Card" />
            </div>

            <div className="mb-3">
              <label className="block text-[9px] font-bold text-gray-500 uppercase tracking-wide mb-1">Client / Framework</label>
              <input value={cardForm.client} onChange={e => setCardForm(f => ({ ...f, client: e.target.value }))}
                className="w-full border border-gray-200 rounded px-2 py-1.5 text-[11px] focus:outline-none focus:border-gold"
                placeholder="e.g. RRSL" />
            </div>

            <div className="mb-3">
              <label className="block text-[9px] font-bold text-gray-500 uppercase tracking-wide mb-1">Effective From *</label>
              <input type="date" value={cardForm.effective_from} onChange={e => setCardForm(f => ({ ...f, effective_from: e.target.value }))}
                className="w-full border border-gray-200 rounded px-2 py-1.5 text-[11px] focus:outline-none focus:border-gold" />
            </div>

            <div className="mb-4 flex items-center gap-2">
              <input id="card-active" type="checkbox" checked={cardForm.is_active} onChange={e => setCardForm(f => ({ ...f, is_active: e.target.checked }))}
                className="accent-gold" />
              <label htmlFor="card-active" className="text-[10px] font-semibold text-gray-600 cursor-pointer">
                Set as active rate card
              </label>
            </div>
            {cardForm.is_active && (
              <p className="text-[9px] text-amber-600 mb-3 -mt-2 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                All other rate cards will be deactivated.
              </p>
            )}

            <div className="flex gap-2">
              <button onClick={() => setCardModal(null)} className="flex-1 text-[11px] border border-gray-200 rounded py-2 font-semibold text-gray-500 hover:bg-gray-50">Cancel</button>
              <button onClick={submitCard} disabled={savingCard} className="flex-1 text-[11px] bg-navy text-white rounded py-2 font-bold hover:opacity-90 disabled:opacity-50">
                {savingCard ? 'Saving…' : cardModal.mode === 'create' ? 'Create' : 'Save Changes'}
              </button>
            </div>
        </Modal>
      )}

      {/* ── Band modal ────────────────────────────────────────────────────────── */}
      {bandModal && (
        <Modal title={bandModal.mode === 'create' ? 'Add Band' : 'Edit Band'} onClose={() => setBandModal(null)}>

            <div className="mb-3">
              <label className="block text-[9px] font-bold text-gray-500 uppercase tracking-wide mb-1">Band Code *</label>
              <input value={bandForm.band_code}
                onChange={e => setBandForm(f => ({ ...f, band_code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') }))}
                className="w-full border border-gray-200 rounded px-2 py-1.5 text-[11px] font-mono focus:outline-none focus:border-gold"
                placeholder="e.g. B5" maxLength={8} />
            </div>

            <div className="mb-3">
              <label className="block text-[9px] font-bold text-gray-500 uppercase tracking-wide mb-1">Band Name</label>
              <input value={bandForm.band_name} onChange={e => setBandForm(f => ({ ...f, band_name: e.target.value }))}
                className="w-full border border-gray-200 rounded px-2 py-1.5 text-[11px] focus:outline-none focus:border-gold"
                placeholder="e.g. Senior Technical Specialist Band 5" />
            </div>

            <div className="mb-4">
              <label className="block text-[9px] font-bold text-gray-500 uppercase tracking-wide mb-1">Day Rate (£) *</label>
              <input type="number" min="0" step="0.01" value={bandForm.day_rate}
                onChange={e => setBandForm(f => ({ ...f, day_rate: e.target.value }))}
                className="w-full border border-gray-200 rounded px-2 py-1.5 text-[11px] focus:outline-none focus:border-gold"
                placeholder="e.g. 895.53" />
            </div>

            <div className="flex gap-2">
              <button onClick={() => setBandModal(null)} className="flex-1 text-[11px] border border-gray-200 rounded py-2 font-semibold text-gray-500 hover:bg-gray-50">Cancel</button>
              <button onClick={submitBand} disabled={savingBand} className="flex-1 text-[11px] bg-navy text-white rounded py-2 font-bold hover:opacity-90 disabled:opacity-50">
                {savingBand ? 'Saving…' : bandModal.mode === 'create' ? 'Add Band' : 'Save Changes'}
              </button>
            </div>
        </Modal>
      )}
    </div>
  );
}
